// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState, useEffect } from 'react';
import CategorySelector from './components/CategorySelector';
import VoiceRecorder from './components/VoiceRecorder';
import FeedbackResults from './components/FeedbackResults';
import InsightsDashboard from './components/InsightsDashboard';
import BrandingAdmin from './components/BrandingAdmin';
import DiscoverExperience from './components/DiscoverExperience';
import Header from './components/Header';
import TenantLoader from './components/TenantLoader';
import { applyTenantTheme } from './tenantTheme';
import './App.css';

const STEPS = {
  CATEGORY: 'category',
  RECORD: 'record',
  PROCESSING: 'processing',
  RESULTS: 'results',
};

const VIEWS = {
  FEEDBACK: 'feedback',
  INSIGHTS: 'insights',
};

/**
 * Extract the tenant slug and view from the current URL path.
 * Patterns:
 *   /t/{slug}             → tenant feedback
 *   /t/{slug}/insights    → tenant insights
 *   /admin                → platform admin (separate flow)
 *   /                     → landing / redirect
 */
function parseRoute() {
  const path = window.location.pathname;

  if (path === '/admin' || path.startsWith('/admin/')) {
    return { page: 'admin', slug: null };
  }

  if (path === '/discover' || path.startsWith('/discover/')) {
    return { page: 'discover', slug: null };
  }

  const match = path.match(/^\/t\/([a-z0-9-]+)(\/.*)?$/);
  if (match) {
    const slug = match[1];
    const sub = match[2] || '';
    if (sub.startsWith('/insights')) return { page: 'tenant', slug, initialView: 'insights' };
    return { page: 'tenant', slug, initialView: 'feedback' };
  }

  return { page: 'landing', slug: null };
}

export default function App() {
  const [route] = useState(parseRoute);
  const [tenantConfig, setTenantConfig] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState(null);
  const [accessCode, setAccessCode] = useState(() => sessionStorage.getItem('cc_access_code') || '');
  const [accessGateVisible, setAccessGateVisible] = useState(false);

  const [view, setView] = useState(route.initialView || VIEWS.FEEDBACK);
  const [step, setStep] = useState(STEPS.CATEGORY);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [feedbackResult, setFeedbackResult] = useState(null);
  const [error, setError] = useState(null);

  // Load tenant config on mount
  useEffect(() => {
    if (route.page !== 'tenant' || !route.slug) {
      setTenantLoading(false);
      return;
    }

    const API_BASE = process.env.REACT_APP_API_URL || '/api';
    fetch(`${API_BASE}/t/${route.slug}/config`)
      .then((res) => {
        if (!res.ok) throw new Error('Organization not found');
        return res.json();
      })
      .then((data) => {
        setTenantConfig(data);
        applyTenantTheme(data.branding);
        // Show access gate if required and no code stored
        if (data.requiresAccessCode && !sessionStorage.getItem('cc_access_code')) {
          setAccessGateVisible(true);
        }
      })
      .catch((err) => setTenantError(err.message))
      .finally(() => setTenantLoading(false));
  }, [route.page, route.slug]);

  function handleCategorySelected(category) {
    setSelectedCategory(category);
    setStep(STEPS.RECORD);
  }

  function handleRecordingComplete(result) {
    setFeedbackResult(result);
    setStep(STEPS.RESULTS);
  }

  function handleProcessingStart() {
    setStep(STEPS.PROCESSING);
  }

  function handleError(message) {
    setError(message);
    setStep(STEPS.RECORD);
  }

  function handleStartOver() {
    setStep(STEPS.CATEGORY);
    setSelectedCategory(null);
    setFeedbackResult(null);
    setError(null);
  }

  function handleBack() {
    if (step === STEPS.RECORD) {
      setStep(STEPS.CATEGORY);
      setSelectedCategory(null);
      setError(null);
    }
  }

  function switchView(nextView) {
    setView(nextView);
    if (nextView === VIEWS.FEEDBACK) handleStartOver();
  }

  // ── Admin page ────────────────────────────────────────────────────────
  if (route.page === 'admin') {
    return <BrandingAdmin />;
  }

  // ── Discover experience ───────────────────────────────────────────────
  if (route.page === 'discover') {
    return <DiscoverExperience />;
  }

  // ── Landing page (no tenant slug) ─────────────────────────────────────
  if (route.page === 'landing') {
    return (
      <div className="app">
        <div className="app__landing">
          <h1>Voice Feedback Platform</h1>
          <p>Maintain the conversation with your members, don't send them surveys.</p>
        </div>
      </div>
    );
  }

  // ── Tenant loading / error states ─────────────────────────────────────
  if (tenantLoading) {
    return <TenantLoader />;
  }

  if (tenantError || !tenantConfig) {
    return (
      <div className="app">
        <div className="app__landing">
          <h1>Organization Not Found</h1>
          <p>The URL you visited doesn't match an active organization. Please check the link provided by your administrator.</p>
        </div>
      </div>
    );
  }

  // ── Tenant app ────────────────────────────────────────────────────────
  const apiBase = `${process.env.REACT_APP_API_URL || '/api'}/t/${route.slug}`;

  // Access code gate
  if (accessGateVisible) {
    return (
      <div className="app">
        <div className="app__landing">
          <h1>{tenantConfig.branding?.clubName || tenantConfig.name}</h1>
          <p style={{ marginBottom: 20 }}>Enter the access code provided by your organization to continue.</p>
          <form
            className="access-gate"
            onSubmit={(e) => {
              e.preventDefault();
              sessionStorage.setItem('cc_access_code', accessCode);
              setAccessGateVisible(false);
            }}
          >
            <input
              type="text"
              className="access-gate__input"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
              placeholder="ACCESS CODE"
              maxLength={10}
              autoFocus
              aria-label="Access code"
            />
            <button type="submit" className="access-gate__btn" disabled={!accessCode.trim()}>
              Enter
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Pass access code header to all API calls
  const apiHeaders = tenantConfig.requiresAccessCode ? { 'X-Access-Code': accessCode } : {};

  return (
    <div className="app">
      <Header
        activeView={view}
        onSwitchView={tenantConfig.settings?.showInsights !== false ? switchView : null}
        branding={tenantConfig.branding}
      />
      <main className="app__main">
        {view === VIEWS.INSIGHTS ? (
          <div className="app__container app__container--wide">
            <InsightsDashboard apiBase={apiBase} tenantConfig={tenantConfig} apiHeaders={apiHeaders} />
          </div>
        ) : (
          <div className="app__container">
            {step === STEPS.CATEGORY && (
              <CategorySelector
                categories={tenantConfig.categories}
                onSelect={handleCategorySelected}
              />
            )}
            {(step === STEPS.RECORD || step === STEPS.PROCESSING) && (
              <VoiceRecorder
                category={selectedCategory}
                isProcessing={step === STEPS.PROCESSING}
                onProcessingStart={handleProcessingStart}
                onComplete={handleRecordingComplete}
                onError={handleError}
                onBack={handleBack}
                error={error}
                apiBase={apiBase}
                apiHeaders={apiHeaders}
              />
            )}
            {step === STEPS.RESULTS && (
              <FeedbackResults
                result={feedbackResult}
                category={selectedCategory}
                onStartOver={handleStartOver}
              />
            )}
          </div>
        )}
      </main>
      <footer className="app__footer">
        <p>Your feedback helps us improve the experience for all members.</p>
        <p className="app__copyright">© 2026 Otyka LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}
